import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FaUpload, FaSave, FaTimes } from 'react-icons/fa';
import Loader from '../ui/Loader';
import api from '../../utils/api';

// This function centers the initial crop with fixed aspect ratio
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const TeamImageCropper = ({ member, onSave, onCancel, onLocalUpdate, apiAvailable = true }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  // Fixed 3:4 aspect ratio for portrait photos (non-changeable)
  const aspect = 3 / 4;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Load the existing image if member has one
  useEffect(() => {
    if (member && member.image) {
      // We add a timestamp to prevent caching
      setImgSrc(`${member.image}?timestamp=${new Date().getTime()}`);
    }
  }, [member]);
  
  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Reset crop
      const file = e.target.files[0];
      setUploadedFile(file);
      
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result));
      reader.readAsDataURL(file);
    }
  }
  
  function onImageLoad(e) {
    // Always use the fixed aspect ratio
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  }
  
  // Generate a cropped image with proper aspect ratio
  function generateCroppedImage(crop, scale = 1, rotate = 0) {
    if (!imgRef.current || !crop.width || !crop.height || !canvasRef.current) {
      return null;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Get image dimensions from the original image element
    const imageWidth = imgRef.current.width;
    const imageHeight = imgRef.current.height;
    
    // Calculate scaling factors based on natural image dimensions vs. displayed dimensions
    const scaleX = imgRef.current.naturalWidth / imageWidth;
    const scaleY = imgRef.current.naturalHeight / imageHeight;
    
    // Set canvas size to desired output size (final crop size)
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Use the scaled crop dimensions for the canvas
    canvas.width = Math.round(crop.width * scaleX);
    canvas.height = Math.round(crop.height * scaleY);
    
    // Clear the canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Save the current context state
    ctx.save();
    
    // Calculate the crop coordinates
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;
    
    // Draw the cropped image
    ctx.drawImage(
      imgRef.current,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, canvas.width, canvas.height
    );
    
    // Apply scale and rotation if needed
    if (scale !== 1 || rotate !== 0) {
      // Create a temporary canvas for the transformations
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Copy the current canvas to the temporary one
      tempCtx.drawImage(canvas, 0, 0);
      
      // Clear the original canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      
      // Move to center, scale, rotate, then draw
      ctx.translate(canvas.width/2, canvas.height/2);
      ctx.scale(scale, scale);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.translate(-canvas.width/2, -canvas.height/2);
      
      // Draw the transformed image back to the original canvas
      ctx.drawImage(tempCanvas, 0, 0);
      ctx.restore();
    }
    
    ctx.restore();
    
    return canvas;
  }
  
  const handleSave = async () => {
    if (!completedCrop || !imgRef.current) {
      setError('请先裁剪图片');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      
      // Generate canvas with cropped image
      const canvas = generateCroppedImage(completedCrop, scale, rotate);
      
      if (!canvas) {
        throw new Error('图片裁剪失败');
      }
      
      // Convert canvas to blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
      });
      
      if (!blob) {
        throw new Error('图片格式转换失败');
      }
      
      // Create a file from the blob
      const filename = `team_${member.id}_${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      
      // If API is not available, use local update instead
      if (!apiAvailable) {
        // Call the onLocalUpdate function
        onLocalUpdate(member.id, file);
        return;
      }
      
      // Create FormData for API call
      const formData = new FormData();
      formData.append('memberImage', file);
      formData.append('memberId', member.id.toString()); // Ensure memberId is a string
      
      try {
        // Make the API call
        const response = await api.post('/team/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Call the onSave callback with new image path
        if (response.data && response.data.success) {
          onSave(member.id, response.data.imagePath);
        } else {
          throw new Error(response.data?.message || '上传失败');
        }
      } catch (apiError) {
        console.error('API upload error:', apiError);
        
        // If API call fails, fall back to local update
        const localImagePath = URL.createObjectURL(file);
        onSave(member.id, localImagePath);
        
        // Show warning that this is a temporary solution
        setError('服务器上传失败，图片已暂时更新但不会永久保存。');
      }
    } catch (err) {
      console.error('Image processing error:', err);
      // Improved error handling
      const errorMessage = err.message || '图片处理失败';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="team-image-cropper bg-white p-4 rounded-xl shadow-lg max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold">编辑团队成员照片</h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <FaTimes size={20} />
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 text-red-800 p-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}
      
      {!apiAvailable && (
        <div className="bg-yellow-100 text-yellow-800 p-2 rounded mb-3 text-sm">
          注意：图片上传功能暂时不可用。您的更改将仅在此会话中可见，不会保存到服务器。
        </div>
      )}
      
      <div className="mb-4">
        <div className="flex items-center justify-center p-2 border-2 border-dashed border-gray-300 rounded-lg mb-3">
          {!imgSrc ? (
            <div className="text-center py-8">
              <FaUpload className="mx-auto text-gray-400 text-3xl mb-2" />
              <p className="text-gray-500 text-sm">请选择或拖放图片</p>
            </div>
          ) : (
            <div className="cropper-container max-h-[350px] overflow-hidden">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                locked={true} // Lock the aspect ratio so it can't be changed
                minWidth={100}
                circularCrop={false}
              >
                <img
                  ref={imgRef}
                  alt="Team member"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  style={{ 
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxHeight: '350px',
                    width: 'auto'
                  }}
                />
              </ReactCrop>
            </div>
          )}
        </div>
        
        <div className="flex justify-center mb-3">
          <label className="inline-block bg-red-700 text-white px-3 py-1 rounded text-sm hover:bg-red-800 transition-colors cursor-pointer">
            <FaUpload className="inline mr-1" />
            选择照片
            <input
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              className="hidden"
            />
          </label>
        </div>
        
        {imgSrc && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                缩放: {scale.toFixed(2)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.01"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="w-full h-4"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                旋转: {rotate}°
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotate}
                onChange={(e) => setRotate(Number(e.target.value))}
                className="w-full h-4"
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 mt-2 mb-1">
              <p className="font-medium">提示：</p>
              <p>此编辑器将保持3:4的固定比例，确保照片显示统一美观。</p>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-between sticky bottom-0 pt-2 bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
          disabled={uploading}
        >
          取消
        </button>
        
        <button
          type="button"
          onClick={handleSave}
          className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 flex items-center justify-center min-w-[80px]"
          disabled={!completedCrop || uploading}
        >
          {uploading ? (
            <Loader size="sm" />
          ) : (
            <>
              <FaSave className="mr-1" />
              保存
            </>
          )}
        </button>
      </div>
      
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default TeamImageCropper;